import React, { memo } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { User, EditingId, EditingPharmacy } from '../types';
import { ROLE_MAP } from '../constants';
import EditableCell from './EditableCell';

interface UserTableRowProps {
  user: User;
  shouldShowIIN: boolean;
  editingId: EditingId | null;
  editingPharmacy: EditingPharmacy | null;
  canEditId: (userRole: string) => boolean;
  canEditPharmacyNum: (userRole: string) => boolean;
  onStartEditingId: (user: User) => void;
  onStartEditingPharmacy: (user: User) => void;
  onSaveNewId: (userId: number, newId: string) => void;
  onSaveNewPharmacyNum: (userId: number, newPharmacyNum: string) => void;
  onIdChange: (newId: string) => void;
  onPharmacyChange: (newPharmacyNum: string) => void;
  onCancelIdEdit: () => void;
  onCancelPharmacyEdit: () => void;
  isGuide: boolean;
}

const formatRole = (role: string): string => {
  return ROLE_MAP[role as keyof typeof ROLE_MAP] || role;
};

const UserTableRow: React.FC<UserTableRowProps> = memo(({
  user,
  shouldShowIIN,
  editingId,
  editingPharmacy,
  canEditId,
  canEditPharmacyNum,
  onStartEditingId,
  onStartEditingPharmacy,
  onSaveNewId,
  onSaveNewPharmacyNum,
  onIdChange,
  onPharmacyChange,
  onCancelIdEdit,
  onCancelPharmacyEdit,
  isGuide
}) => {
  return (
    <TableRow
      sx={{
        padding: '8px',
        height: '53.5px',
        whiteSpace: 'nowrap',
      }}
    >
      <TableCell>{user.username}</TableCell>

      {!isGuide && (
        <EditableCell
          value={editingPharmacy?.userId === user.id ? editingPharmacy.newPharmacyNum : user.pharmacyNum}
          isEditing={editingPharmacy?.userId === user.id}
          canEdit={canEditPharmacyNum(user.role)}
          width="100px"
          type="number"
          onDoubleClick={() => onStartEditingPharmacy(user)}
          onSave={(value) => onSaveNewPharmacyNum(user.id, value)}
          onCancel={onCancelPharmacyEdit}
          onChange={onPharmacyChange}
        />
      )}

      <TableCell sx={{ width: '150px' }}>{user.cityId}</TableCell>
      <TableCell sx={{ width: '150px' }}>{formatRole(user.role)}</TableCell>

      {!isGuide && (
        <EditableCell
          value={editingId?.userId === user.id ? editingId.newId : user.id}
          isEditing={editingId?.userId === user.id}
          canEdit={canEditId(user.role)}
          width="100px"
          type="number"
          onDoubleClick={() => onStartEditingId(user)}
          onSave={(value) => onSaveNewId(user.id, value)}
          onCancel={onCancelIdEdit}
          onChange={onIdChange}
        />
      )}

      <TableCell sx={{ width: '150px' }}>
        <a
          href={`https://wa.me/${user.userphone}`}
          target='_blank'
          rel="noopener noreferrer"
          style={{
            color: 'black',
            outline: 'none',
            textDecoration: 'none',
          }}
        >
          {user.userphone}
        </a>
      </TableCell>

      <TableCell sx={{ width: '150px' }}>{user.fullName}</TableCell>

      {shouldShowIIN && (
        <TableCell
          sx={{
            width: '150px',
            '&:hover': {
              filter: 'none',
              transition: 'filter 0.3s ease'
            },
            filter: 'blur(4px)',
            transition: 'filter 0.3s ease'
          }}
        >
          {user.iin}
        </TableCell>
      )}
    </TableRow>
  );
});

UserTableRow.displayName = 'UserTableRow';

export default UserTableRow;

